import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  HiPlus,
  HiSearch,
  HiCash,
  HiOutlineChatAlt2,
  HiDuplicate,
  HiExternalLink,
  HiTrash,
  HiCheckCircle,
  HiCog,
  HiCloudUpload,
} from 'react-icons/hi';
import apiService from '../services/api';
import { normalizeApiData, normalizeImageUrl } from '../utils/apiHelpers';
import { useNotification } from '../hooks/useNotification';
import Notification from './Notification';
import Select from './Select';
import ClientSearchSelect from './ClientSearchSelect';
import { SkeletonTable } from './Skeleton';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSAO_MAP } from '../types';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';
import { openWhatsAppApp, toWhatsAppDigits } from './OpenWhatsAppButton';
import ConfirmDialog from './ConfirmDialog';
import './Cobrancas.css';

const STATUS_LABELS = {
  pendente: 'Pendente',
  enviada: 'Enviada',
  aguardando_confirmacao: 'Aguardando confirmação',
  incompleto: 'Incompleto',
  paga: 'Paga',
  cancelada: 'Cancelada',
};

const MODO_PAGAMENTO_OPTIONS = [
  { value: 'pix', label: 'PIX' },
  { value: 'dinheiro', label: 'Dinheiro' },
  { value: 'cartao', label: 'Cartão' },
  { value: 'transferencia', label: 'Transferência' },
  { value: 'cheque', label: 'Cheque' },
];

const modoPagamentoLabel = (value) =>
  MODO_PAGAMENTO_OPTIONS.find((o) => o.value === value)?.label || value || '—';

const parseMoneyInput = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const cleaned = String(value)
    .replace(/[^\d,.-]/g, '')
    .trim();
  if (!cleaned) return null;
  const normalized =
    cleaned.includes(',') && cleaned.includes('.')
      ? cleaned.replace(/\./g, '').replace(',', '.')
      : cleaned.replace(',', '.');
  const num = Number(normalized);
  return Number.isFinite(num) ? num : null;
};

const remainingAmount = (item) => {
  if (item?.valor_restante != null) return Math.max(0, Number(item.valor_restante) || 0);
  const total = Number(item?.valor) || 0;
  const paid = Number(item?.valor_pago) || 0;
  return Math.max(0, Math.round((total - paid) * 100) / 100);
};

const comprovantesOf = (item) => {
  if (Array.isArray(item?.comprovantes) && item.comprovantes.length > 0) {
    return item.comprovantes
      .map((c) =>
        typeof c === 'string'
          ? { url: c, nome: c.split('/').pop() }
          : { url: c?.url, nome: c?.nome || c?.url?.split('/').pop() }
      )
      .filter((c) => c.url);
  }
  if (item?.comprovante_url) {
    return [{ url: item.comprovante_url, nome: item.comprovante_url.split('/').pop() }];
  }
  return [];
};

const getPackageDestino = (pkg) => {
  const raw =
    pkg?.destino ??
    pkg?.cidade_destino ??
    pkg?.tarifa?.destino ??
    pkg?.endereco_destino?.cidade ??
    pkg?.endereco_destino?.nome ??
    '';
  return String(raw).trim();
};

const buildCobrancaTitulo = (pkg) => {
  const destino = getPackageDestino(pkg);
  if (destino) return `Cobrança da carga para ${destino}`;
  const code = pkg?.codigo_pacote || pkg?.id;
  return code ? `Cobrança da carga ${code}` : 'Cobrança da carga';
};

const emptyForm = {
  cliente_id: '',
  cliente_nome: '',
  pacote_id: '',
  titulo: '',
  descricao: '',
  valor: '',
  vencimento: '',
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    Number(value) || 0
  );

const maskCurrencyInput = (value) => {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (!digits) return '';
  return formatCurrency(Number(digits) / 100);
};

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value.includes('T') ? value : `${value}T12:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('pt-BR');
};

const DEFAULT_PAYMENT_BASE = 'https://backoffice.sendingto.com';

const getPaymentUrl = (item, settings) => {
  if (item?.payment_url) return item.payment_url;
  if (!item?.id) return '';
  const base = String(settings?.public_base_url || DEFAULT_PAYMENT_BASE).replace(/\/$/, '');
  return `${base}/pagar/${item.id}`;
};

const buildWhatsAppMessage = ({ clientName, title, amount, dueDate, paymentUrl }) =>
  [
    `Olá${clientName ? ` ${clientName}` : ''}!`,
    '',
    `Segue a cobrança *${title}* no valor de *${amount}*.`,
    `Vencimento: ${dueDate}`,
    '',
    `Pague via PIX pelo link:`,
    paymentUrl,
    '',
    'Após efetuar o pagamento, envie o comprovante por aqui.',
  ].join('\n');

const extractErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  (error?.response?.data?.errors
    ? Object.values(error.response.data.errors).flat().join(' ')
    : null) ||
  error?.message ||
  fallback;

const Cobrancas = () => {
  const { userRole, loading: permissionsLoading } = usePermissions();
  const canManage = useMemo(() => {
    if (permissionsLoading || !userRole) return false;
    const role = PERMISSAO_MAP[Number(userRole.permissaos_id)];
    return role === 'Admin' || role === 'Gerente';
  }, [userRole, permissionsLoading]);

  /** Configurações PIX (chave, merchant, WhatsApp, URL) — só Admin */
  const canManagePixSettings = useMemo(() => {
    if (permissionsLoading || !userRole) return false;
    return PERMISSAO_MAP[Number(userRole.permissaos_id)] === 'Admin';
  }, [userRole, permissionsLoading]);

  const { notifications, removeNotification, showSuccess, showWarning } =
    useNotification();

  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientPackages, setClientPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [filter, setFilter] = useState('todas');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmPaidForm, setConfirmPaidForm] = useState({
    modo_pagamento: 'pix',
    valor: '',
    comprovantes: [],
  });
  const [confirmingPaid, setConfirmingPaid] = useState(false);
  const [settings, setSettings] = useState({
    pix_key: '',
    pix_key_type: 'email',
    pix_merchant_name: 'SENDINGTO',
    pix_merchant_city: 'SAO PAULO',
    whatsapp_number: '+5511989141662',
    public_base_url: DEFAULT_PAYMENT_BASE,
  });

  const loadClients = useCallback(async () => {
    try {
      const response = await apiService.getCobrancaClients({
        page: 1,
        per_page: 500,
        only_pending: true,
      });
      setClients(normalizeApiData(response) || []);
    } catch {
      setClients([]);
    }
  }, []);

  const loadClientPackages = useCallback(async (clienteId) => {
    if (!clienteId) {
      setClientPackages([]);
      return;
    }
    try {
      setLoadingPackages(true);
      const response = await apiService.getCobrancaClientPackages(Number(clienteId), {
        only_pending: true,
      });
      const packages = response?.data?.packages || response?.packages || [];
      setClientPackages(Array.isArray(packages) ? packages : []);
    } catch {
      setClientPackages([]);
    } finally {
      setLoadingPackages(false);
    }
  }, []);

  const loadItems = useCallback(async (opts = {}) => {
    try {
      if (!opts.silent) setLoading(true);
      const response = await apiService.getCobrancasList({
        status: filter === 'todas' ? undefined : filter,
        search: searchTerm || undefined,
        page: currentPage,
        per_page: itemsPerPage,
      });
      const payload = response?.data || response || {};
      const list = normalizeApiData(response) || [];
      setItems(list);
      setTotalItems(Number(payload.total) || list.length);
      setTotalPages(Math.max(1, Number(payload.last_page) || 1));
      const apiPage = Number(payload.current_page);
      if (apiPage && apiPage !== currentPage) {
        setCurrentPage(apiPage);
      }
    } catch (error) {
      console.error(error);
      showWarning(extractErrorMessage(error, 'Erro ao carregar cobranças'));
      setItems([]);
      setTotalItems(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [filter, searchTerm, currentPage, itemsPerPage, showWarning]);

  const loadSettings = useCallback(async () => {
    try {
      const response = await apiService.getCobrancaSettings();
      const data = response?.data || response;
      if (data) setSettings((prev) => ({ ...prev, ...data }));
    } catch {
      // ignore
    }
  }, []);

  const openDetail = useCallback(
    async (item) => {
      if (!item?.id) return;
      setViewItem(item);
      try {
        setLoadingDetail(true);
        const response = await apiService.getCobranca(item.id);
        const detail = response?.data || response;
        if (detail?.id) setViewItem(detail);
      } catch {
        // keep list item snapshot
      } finally {
        setLoadingDetail(false);
      }
    },
    []
  );

  useEffect(() => {
    loadClients();
    loadSettings();
  }, [loadClients, loadSettings]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchTerm]);

  useEffect(() => {
    const t = setTimeout(() => {
      loadItems();
    }, 250);
    return () => clearTimeout(t);
  }, [loadItems]);

  useRealtimeRefresh(
    ['cobrancas', 'payments', 'packages', 'clients'],
    () => loadItems({ silent: true }),
    { paused: showModal }
  );

  const totals = useMemo(() => {
    const open = items.filter((i) =>
      ['pendente', 'enviada', 'aguardando_confirmacao', 'incompleto'].includes(i.status)
    );
    const paid = items.filter((i) => i.status === 'paga');
    return {
      openAmount: open.reduce((s, i) => s + remainingAmount(i), 0),
      paidAmount: paid.reduce((s, i) => s + (Number(i.valor) || 0), 0),
      openCount: open.length,
      paidCount: paid.length,
      total: items.length,
    };
  }, [items]);

  const openCreate = () => {
    setForm(emptyForm);
    setClientPackages([]);
    setShowModal(true);
  };

  const handlePackageChange = (pacoteId) => {
    const pkg = clientPackages.find((p) => String(p.id) === String(pacoteId));
    setForm((prev) => {
      const next = { ...prev, pacote_id: pacoteId };
      if (!pkg) return next;

      const autoTitulo =
        !prev.titulo.trim() ||
        prev.titulo.startsWith('Cobrança da carga') ||
        prev.titulo.startsWith('Pagamento ');
      if (autoTitulo) {
        next.titulo = buildCobrancaTitulo(pkg);
      }
      // Prefere o restante (editável) — permite gerar PIX parcial
      const restante = pkg?.valor_restante;
      const totalPkg = pkg?.valor ?? pkg?.valor_estimado ?? pkg?.valor_declarado;
      const sugestao = restante != null && Number(restante) > 0 ? restante : totalPkg;
      if (sugestao) {
        next.valor = String(sugestao);
      }
      return next;
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.cliente_id || !form.titulo || !form.valor || !form.vencimento) {
      showWarning('Preencha cliente, título, valor e vencimento');
      return;
    }
    const valor = parseMoneyInput(form.valor);
    if (!valor || valor <= 0) {
      showWarning('Informe um valor válido');
      return;
    }
    if (form.pacote_id) {
      const pkg = clientPackages.find((p) => String(p.id) === String(form.pacote_id));
      const restante = Number(pkg?.valor_restante);
      if (Number.isFinite(restante) && restante >= 0 && valor > restante + 0.009) {
        showWarning(
          `Valor do PIX maior que o restante da caixa/mala (${formatCurrency(restante)})`
        );
        return;
      }
    }
    try {
      setSaving(true);
      const response = await apiService.createCobranca({
        cliente_id: Number(form.cliente_id),
        pacote_id: form.pacote_id ? Number(form.pacote_id) : null,
        titulo: form.titulo.trim(),
        descricao: form.descricao?.trim() || undefined,
        valor,
        vencimento: form.vencimento,
      });
      const created = response?.data || response;
      setShowModal(false);
      setViewItem(created);
      showSuccess('Cobrança criada');
      await Promise.all([loadItems(), loadClients()]);
    } catch (error) {
      showWarning(extractErrorMessage(error, 'Erro ao criar'));
    } finally {
      setSaving(false);
    }
  };

  const handleSendWhatsApp = async (item) => {
    try {
      const response = await apiService.sendCobranca(item.id);
      const data = response?.data || response;
      const cobranca = data?.cobranca || item;
      const paymentUrl =
        data?.payment_url || getPaymentUrl(cobranca, settings) || cobranca.payment_url;

      // Prioriza telefone retornado pela API (cliente_telefone)
      const clientFromList = clients.find(
        (c) => String(c.id) === String(cobranca.cliente_id || item.cliente_id)
      );
      const phone =
        data?.cliente_telefone ||
        cobranca?.cliente_telefone ||
        item.cliente_telefone ||
        clientFromList?.telefone ||
        '';

      try {
        if (paymentUrl) {
          await navigator.clipboard.writeText(paymentUrl);
        }
      } catch {
        // ignore
      }

      const digits = toWhatsAppDigits(phone);
      if (!digits) {
        showWarning(
          'Cliente sem telefone (cliente_telefone). Cadastre o telefone do cliente para enviar no WhatsApp. O link foi copiado.'
        );
        setViewItem(cobranca);
        await loadItems();
        return;
      }

      const message = buildWhatsAppMessage({
        clientName: cobranca.cliente_nome || item.cliente_nome || clientFromList?.nome,
        title: cobranca.titulo || item.titulo,
        amount: formatCurrency(cobranca.valor ?? item.valor),
        dueDate: formatDate(cobranca.vencimento || item.vencimento),
        paymentUrl,
      });

      const opened = openWhatsAppApp(phone, message);
      if (!opened) {
        showWarning('Não foi possível abrir o WhatsApp App. Verifique o telefone do cliente.');
        await loadItems();
        return;
      }

      setViewItem(cobranca);
      showSuccess(
        `WhatsApp App aberto para ${cobranca.cliente_nome || item.cliente_nome || 'o cliente'}`
      );
      await loadItems();
    } catch (error) {
      showWarning(extractErrorMessage(error, 'Erro ao enviar'));
    }
  };
  const revokeComprovantePreviews = (files) => {
    (files || []).forEach((entry) => {
      if (entry?.preview) URL.revokeObjectURL(entry.preview);
    });
  };

  const requestMarkPaid = (item) => {
    setConfirmPaidForm((prev) => {
      revokeComprovantePreviews(prev.comprovantes);
      const restante = remainingAmount(item);
      return {
        modo_pagamento: 'pix',
        valor: restante > 0 ? formatCurrency(restante) : '',
        comprovantes: [],
      };
    });
    setConfirmAction({ type: 'paid', item });
  };

  const requestDelete = (item) => {
    setConfirmAction({ type: 'delete', item });
  };

  const clearComprovantes = () => {
    setConfirmPaidForm((prev) => {
      revokeComprovantePreviews(prev.comprovantes);
      return { ...prev, comprovantes: [] };
    });
  };

  const removeComprovanteAt = (index) => {
    setConfirmPaidForm((prev) => {
      const next = [...(prev.comprovantes || [])];
      const [removed] = next.splice(index, 1);
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      return { ...prev, comprovantes: next };
    });
  };

  const handleComprovanteChange = (e) => {
    const selected = Array.from(e.target.files || []);
    e.target.value = '';
    if (selected.length === 0) return;

    const allowed = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/webp',
    ];
    const valid = [];
    for (const file of selected) {
      if (!allowed.includes(file.type) && !/\.(pdf|jpe?g|png|webp)$/i.test(file.name)) {
        showWarning(`Arquivo inválido: ${file.name}`);
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        showWarning(`${file.name} excede 10 MB`);
        continue;
      }
      valid.push({
        file,
        name: file.name,
        preview: file.type?.startsWith('image/') ? URL.createObjectURL(file) : null,
      });
    }
    if (valid.length === 0) return;

    setConfirmPaidForm((prev) => ({
      ...prev,
      comprovantes: [...(prev.comprovantes || []), ...valid],
    }));
  };

  const handleMarkPaid = async (item) => {
    if (!confirmPaidForm.modo_pagamento) {
      showWarning('Selecione o modo de pagamento');
      return;
    }
    const valorPagoAgora = parseMoneyInput(confirmPaidForm.valor);
    if (valorPagoAgora === null || valorPagoAgora <= 0) {
      showWarning('Informe o valor deste pagamento');
      return;
    }
    const restante = remainingAmount(item);
    if (valorPagoAgora > restante + 0.009) {
      showWarning(`O valor não pode ser maior que o restante (${formatCurrency(restante)})`);
      return;
    }
    const files = (confirmPaidForm.comprovantes || [])
      .map((entry) => entry.file)
      .filter((f) => f instanceof File);
    if (files.length === 0) {
      showWarning('Anexe ao menos um comprovante de pagamento');
      return;
    }

    try {
      setConfirmingPaid(true);
      const response = await apiService.createPayment({
        cobranca_id: item.id,
        pacote_id: item.pacote_id || null,
        metodo_pagamento: confirmPaidForm.modo_pagamento,
        valor: valorPagoAgora,
        comprovantes: files,
      });
      const freshResponse = await apiService.getCobranca(item.id);
      const updated = freshResponse?.data || freshResponse;
      setViewItem(updated);
      setConfirmPaidForm((prev) => {
        revokeComprovantePreviews(prev.comprovantes);
        return { modo_pagamento: 'pix', valor: '', comprovantes: [] };
      });
      setConfirmAction(null);
      showSuccess(response?.message || 'Pagamento registrado');
      await Promise.all([loadItems(), loadClients()]);
    } catch (error) {
      showWarning(extractErrorMessage(error, 'Erro ao confirmar pagamento'));
    } finally {
      setConfirmingPaid(false);
    }
  };

  const handleCancel = async (item) => {
    try {
      const response = await apiService.updateCobrancaStatus(item.id, 'cancelada');
      const updated = response?.data || response;
      showSuccess('Cobrança cancelada');
      setViewItem(updated?.id ? updated : null);
      await loadItems();
    } catch (error) {
      showWarning(extractErrorMessage(error, 'Erro ao cancelar'));
    }
  };

  const handleDelete = async (item) => {
    try {
      await apiService.deleteCobranca(item.id);
      showSuccess('Cobrança excluída');
      setViewItem(null);
      await Promise.all([loadItems(), loadClients()]);
    } catch (error) {
      showWarning(extractErrorMessage(error, 'Erro ao excluir'));
    }
  };

  const handleConfirmAction = async () => {
    if (!confirmAction?.item) return;
    const { type, item } = confirmAction;
    if (type === 'paid') {
      await handleMarkPaid(item);
      return;
    }
    setConfirmAction(null);
    if (type === 'delete') {
      await handleDelete(item);
    }
  };

  const copyText = async (text, successMessage = 'Copiado') => {
    if (!text) {
      showWarning('Nada para copiar');
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      showSuccess(successMessage);
    } catch {
      showWarning('Não foi possível copiar');
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const response = await apiService.updateCobrancaSettings(settings);
      const data = response?.data || response;
      if (data) setSettings((prev) => ({ ...prev, ...data }));
      showSuccess('Configurações PIX salvas');
      setShowSettings(false);
      await loadSettings();
    } catch (error) {
      showWarning(extractErrorMessage(error, 'Erro ao salvar'));
    } finally {
      setSaving(false);
    }
  };

  const statusTone = (status) => {
    if (status === 'paga') return 'success';
    if (status === 'enviada') return 'info';
    if (status === 'aguardando_confirmacao') return 'warning';
    if (status === 'incompleto') return 'warning';
    if (status === 'cancelada') return 'danger';
    return 'neutral';
  };

  const packageOptions = useMemo(
    () => [
      { value: '', label: 'Sem vínculo com caixa/mala' },
      ...clientPackages.map((p) => {
        const valorPkg = p.valor ?? p.valor_estimado ?? p.valor_declarado;
        const destino = getPackageDestino(p);
        return {
          value: String(p.id),
          label: `${p.has_pagamento_pendente ? '⚠ ' : ''}${
            p.codigo_pacote || `ID ${p.id}`
          }${p.tipo ? ` — ${p.tipo}` : ''}${
            destino ? ` — ${destino}` : ''
          }${
            valorPkg != null && valorPkg !== ''
              ? ` — ${formatCurrency(valorPkg)}`
              : ''
          }${
            Number(p.valor_pago) > 0
              ? ` — pago ${formatCurrency(p.valor_pago)} · resta ${formatCurrency(p.valor_restante)}`
              : ''
          } (${p.status_pagamento || 'Pendente'})`,
        };
      }),
    ],
    [clientPackages]
  );

  return (
    <div className="page-container cobrancas-page">
      <div className="breadcrumb">
        <span>Painel</span>
        <span>Cobranças PIX</span>
      </div>

      <section className="cobrancas-hero">
        <div className="cobrancas-hero-left">
          <div className="cobrancas-hero-icon">
            <HiCash size={28} />
          </div>
          <div>
            <h1>Cobranças PIX</h1>
            <p>
              Controle financeiro premium — acompanhe pendências, confirme pagamentos e envie
              cobranças pelo WhatsApp.
            </p>
          </div>
        </div>
        <div className="cobrancas-header-actions">
          {canManagePixSettings && (
            <button type="button" className="btn-cob-ghost" onClick={() => setShowSettings(true)}>
              <HiCog size={18} /> Configurar PIX
            </button>
          )}
          {canManage && (
            <button type="button" className="btn-cob-primary" onClick={openCreate}>
              <HiPlus size={20} /> Nova cobrança
            </button>
          )}
        </div>
      </section>

      <div className="cobrancas-kpis">
        <div className="cobrancas-kpi cobrancas-kpi--open">
          <div className="cobrancas-kpi-label">
            <span>Em aberto</span>
            <i className="cobrancas-kpi-dot" />
          </div>
          <strong>{formatCurrency(totals.openAmount)}</strong>
          <small>{totals.openCount} cobrança(s) a receber</small>
        </div>
        <div className="cobrancas-kpi cobrancas-kpi--paid">
          <div className="cobrancas-kpi-label">
            <span>Recebido</span>
            <i className="cobrancas-kpi-dot" />
          </div>
          <strong>{formatCurrency(totals.paidAmount)}</strong>
          <small>{totals.paidCount} paga(s)</small>
        </div>
        <div className="cobrancas-kpi cobrancas-kpi--total">
          <div className="cobrancas-kpi-label">
            <span>No filtro</span>
            <i className="cobrancas-kpi-dot" />
          </div>
          <strong>{totals.total}</strong>
          <small>registros listados</small>
        </div>
      </div>

      <div className="cobrancas-toolbar">
        <div className="search-box">
          <HiSearch className="search-icon" size={20} />
          <input
            type="text"
            placeholder="Buscar por título, cliente, pacote ou ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="cobrancas-filters">
          {[
            ['todas', 'Todas'],
            ['em_aberto', 'Em aberto'],
            ['incompleto', 'Incompleto'],
            ['enviada', 'Enviada'],
            ['paga', 'Paga'],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={`cobrancas-filter ${filter === value ? 'active' : ''}`}
              onClick={() => setFilter(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="cobrancas-panel">
        {loading ? (
          <SkeletonTable rows={8} columns={8} />
        ) : items.length === 0 ? (
          <div className="cobrancas-empty">
            <div className="cobrancas-empty-icon">
              <HiCash size={28} />
            </div>
            <h3>Nenhuma cobrança neste filtro</h3>
            <p>Crie uma nova cobrança PIX ou ajuste os filtros acima.</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Cobrança</th>
                  <th>Cliente</th>
                  <th>Caixa/Mala</th>
                  <th>Valor</th>
                  <th>Vencimento</th>
                  <th>Status</th>
                  <th>Link</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const paymentUrl = getPaymentUrl(item, settings);
                  const pago = Number(item.valor_pago) || 0;
                  const restante = remainingAmount(item);
                  return (
                    <tr key={item.id}>
                      <td className="cobrancas-title-cell">
                        <strong>{item.titulo}</strong>
                        <span className="cobrancas-id-chip">{item.id}</span>
                      </td>
                      <td>{item.cliente_nome || '—'}</td>
                      <td>
                        {item.pacote_codigo ||
                          (item.pacote_id ? `ID ${item.pacote_id}` : '—')}
                      </td>
                      <td className="cobrancas-amount">
                        <div>{formatCurrency(item.valor)}</div>
                        {(pago > 0 || item.status === 'incompleto') && (
                          <small className="cobrancas-amount-sub">
                            Pago {formatCurrency(pago)} · Resta {formatCurrency(restante)}
                          </small>
                        )}
                      </td>
                      <td>{formatDate(item.vencimento)}</td>
                      <td>
                        <span className={`cobrancas-badge ${statusTone(item.status)}`}>
                          {STATUS_LABELS[item.status] || item.status}
                        </span>
                      </td>
                      <td>
                        <div className="cobrancas-link-cell">
                          <button
                            type="button"
                            className="btn-icon"
                            title="Copiar link"
                            onClick={() => copyText(paymentUrl, 'Link copiado')}
                          >
                            <HiDuplicate size={18} />
                          </button>
                          <a
                            className="btn-icon"
                            href={paymentUrl}
                            target="_blank"
                            rel="noreferrer"
                            title="Abrir link"
                          >
                            <HiExternalLink size={18} />
                          </a>
                        </div>
                      </td>
                      <td>
                        <div className="actions">
                          <button
                            type="button"
                            className="btn-icon"
                            title="Detalhes"
                            onClick={() => openDetail(item)}
                          >
                            <HiCash size={18} />
                          </button>
                          {canManage &&
                            item.status !== 'paga' &&
                            item.status !== 'cancelada' && (
                              <>
                                <button
                                  type="button"
                                  className="btn-icon"
                                  title="Enviar WhatsApp"
                                  onClick={() => handleSendWhatsApp(item)}
                                >
                                  <HiOutlineChatAlt2 size={18} />
                                </button>
                                <button
                                  type="button"
                                  className="btn-icon"
                                  title="Registrar pagamento"
                                  onClick={() => requestMarkPaid(item)}
                                >
                                  <HiCheckCircle size={18} />
                                </button>
                              </>
                            )}
                          {canManage && item.status !== 'paga' && (
                            <button
                              type="button"
                              className="btn-icon"
                              title="Excluir"
                              onClick={() => requestDelete(item)}
                            >
                              <HiTrash size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="table-footer">
              <div className="pagination-info">
                {totalItems > 0 ? (
                  <span>
                    Mostrando {(currentPage - 1) * itemsPerPage + 1} a{' '}
                    {Math.min(currentPage * itemsPerPage, totalItems)} de {totalItems} registro
                    {totalItems !== 1 ? 's' : ''}
                  </span>
                ) : (
                  <span>Nenhum registro encontrado</span>
                )}
              </div>
              <div className="items-per-page">
                <span>Mostrar:</span>
                <Select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="select-sm"
                  options={[
                    { value: 5, label: '5' },
                    { value: 10, label: '10' },
                    { value: 25, label: '25' },
                    { value: 50, label: '50' },
                  ]}
                />
              </div>
              <div className="pagination">
                <button
                  type="button"
                  className="pagination-btn"
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  ‹
                </button>
                <span className="pagination-info">
                  Página {currentPage} de {totalPages || 1}
                </span>
                <button
                  type="button"
                  className="pagination-btn"
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Nova cobrança PIX</h2>
              <button type="button" className="modal-close" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>
            <form className="modal-body" onSubmit={handleCreate}>
              <div className="form-group">
                <label>Cliente *</label>
                <ClientSearchSelect
                  id="field-cobranca-cliente_id"
                  value={form.cliente_id}
                  selectedNome={form.cliente_nome}
                  clients={clients}
                  onRefreshClients={loadClients}
                  allowCreate={false}
                  placeholder="Busque e selecione o cliente..."
                  searchPlaceholder="Buscar por nome ou telefone..."
                  onNotify={({ type, message }) => {
                    if (type === 'success') showSuccess(message);
                    else showWarning(message);
                  }}
                  onChange={({ cliente_id, cliente_nome }) => {
                    setForm((prev) => ({
                      ...prev,
                      cliente_id: cliente_id || '',
                      cliente_nome: cliente_nome || '',
                      pacote_id: '',
                    }));
                    loadClientPackages(cliente_id);
                  }}
                />
                <small className="muted">
                  Apenas clientes com pagamento pendente ou incompleto. Quem já está pago não aparece.
                </small>
              </div>
              <div className="form-group">
                <label>Caixa / Mala (opcional)</label>
                <Select
                  value={form.pacote_id}
                  onChange={(e) => handlePackageChange(e.target.value)}
                  placeholder={
                    !form.cliente_id
                      ? 'Selecione um cliente primeiro...'
                      : loadingPackages
                        ? 'Carregando...'
                        : ''
                  }
                  options={form.cliente_id && !loadingPackages ? packageOptions : []}
                  disabled={!form.cliente_id || loadingPackages}
                />
              </div>
              <div className="form-group">
                <label>Título *</label>
                <input
                  value={form.titulo}
                  onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                  placeholder="Ex.: Frete invoice INV123"
                  required
                />
              </div>
              <div className="form-group">
                <label>Descrição</label>
                <textarea
                  value={form.descricao}
                  onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Valor do PIX (R$) *</label>
                  <input
                    value={form.valor}
                    onChange={(e) => setForm({ ...form, valor: e.target.value })}
                    placeholder="0,00"
                    required
                  />
                  <small style={{ color: '#6b7280', display: 'block', marginTop: 4 }}>
                    Pode ser parcial. Se houver caixa/mala, o máximo é o valor restante.
                  </small>
                </div>
                <div className="form-group">
                  <label>Vencimento *</label>
                  <input
                    type="date"
                    value={form.vencimento}
                    onChange={(e) => setForm({ ...form, vencimento: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cob-muted" onClick={() => setShowModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Salvando…' : 'Criar cobrança'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showSettings && canManagePixSettings && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Configurações PIX</h2>
              <button type="button" className="modal-close" onClick={() => setShowSettings(false)}>
                ×
              </button>
            </div>
            <form className="modal-body" onSubmit={saveSettings}>
              <div className="form-group">
                <label>Chave PIX *</label>
                <input
                  value={settings.pix_key}
                  onChange={(e) => setSettings({ ...settings, pix_key: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Tipo da chave</label>
                <Select
                  value={settings.pix_key_type}
                  onChange={(e) => setSettings({ ...settings, pix_key_type: e.target.value })}
                  options={[
                    { value: 'email', label: 'E-mail' },
                    { value: 'telefone', label: 'Telefone' },
                    { value: 'cpf', label: 'CPF' },
                    { value: 'cnpj', label: 'CNPJ' },
                    { value: 'aleatoria', label: 'Aleatória' },
                  ]}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Nome no PIX</label>
                  <input
                    value={settings.pix_merchant_name}
                    onChange={(e) =>
                      setSettings({ ...settings, pix_merchant_name: e.target.value })
                    }
                    maxLength={25}
                  />
                </div>
                <div className="form-group">
                  <label>Cidade</label>
                  <input
                    value={settings.pix_merchant_city}
                    onChange={(e) =>
                      setSettings({ ...settings, pix_merchant_city: e.target.value })
                    }
                    maxLength={15}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>WhatsApp financeiro</label>
                <input
                  value={settings.whatsapp_number || ''}
                  onChange={(e) =>
                    setSettings({ ...settings, whatsapp_number: e.target.value })
                  }
                  placeholder="+5511989141662"
                />
              </div>
              <div className="form-group">
                <label>URL pública do link (/pagar/...)</label>
                <input
                  value={settings.public_base_url || ''}
                  onChange={(e) =>
                    setSettings({ ...settings, public_base_url: e.target.value })
                  }
                  placeholder={DEFAULT_PAYMENT_BASE}
                />
                <small className="muted">
                  {`Links: ${(settings.public_base_url || DEFAULT_PAYMENT_BASE).replace(/\/$/, '')}/pagar/{id}`}
                </small>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-cob-muted"
                  onClick={() => setShowSettings(false)}
                >
                  Fechar
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  Salvar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {viewItem && (
        <div className="modal-overlay">
          <div className="modal-content modal-large">
            <div className="modal-header">
              <h2>
                {viewItem.titulo}
                {loadingDetail ? ' …' : ''}
              </h2>
              <button type="button" className="modal-close" onClick={() => setViewItem(null)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="cobrancas-detail-grid">
                <div className="cobrancas-detail-item">
                  <span>Cliente</span>
                  <strong>{viewItem.cliente_nome || '—'}</strong>
                </div>
                <div className="cobrancas-detail-item">
                  <span>Status</span>
                  <strong>{STATUS_LABELS[viewItem.status] || viewItem.status}</strong>
                </div>
                <div className="cobrancas-detail-item">
                  <span>Valor</span>
                  <strong>{formatCurrency(viewItem.valor)}</strong>
                </div>
                <div className="cobrancas-detail-item">
                  <span>Vencimento</span>
                  <strong>{formatDate(viewItem.vencimento)}</strong>
                </div>
                <div className="cobrancas-detail-item">
                  <span>Caixa / Mala</span>
                  <strong>
                    {viewItem.pacote_codigo ||
                      (viewItem.pacote_id ? `ID ${viewItem.pacote_id}` : '—')}
                    {viewItem.pacote_tipo ? ` (${viewItem.pacote_tipo})` : ''}
                  </strong>
                </div>
                <div className="cobrancas-detail-item">
                  <span>Pagamento da caixa</span>
                  <strong>{viewItem.pacote_status_pagamento || '—'}</strong>
                </div>
                <div className="cobrancas-detail-item">
                  <span>Valor pago</span>
                  <strong>{formatCurrency(viewItem.valor_pago || 0)}</strong>
                </div>
                <div className="cobrancas-detail-item">
                  <span>Restante a pagar</span>
                  <strong>{formatCurrency(remainingAmount(viewItem))}</strong>
                </div>
                {(Number(viewItem.valor_pago) > 0 || viewItem.status === 'paga' || viewItem.status === 'incompleto') && (
                  <div className="cobrancas-detail-item">
                    <span>Último modo</span>
                    <strong>{modoPagamentoLabel(viewItem.modo_pagamento)}</strong>
                  </div>
                )}
              </div>
              {comprovantesOf(viewItem).length > 0 && (
                <div className="cobrancas-comprovantes-list">
                  <span>Comprovantes</span>
                  <ul>
                    {comprovantesOf(viewItem).map((comp, idx) => (
                      <li key={`${comp.url}-${idx}`}>
                        <a href={normalizeImageUrl(comp.url)} target="_blank" rel="noreferrer">
                          {comp.nome || `Comprovante ${idx + 1}`}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {Array.isArray(viewItem.pagamentos_registrados) && viewItem.pagamentos_registrados.length > 0 && (
                <div className="cobrancas-pagamentos-historico">
                  <span>Histórico de pagamentos</span>
                  <ul>
                    {viewItem.pagamentos_registrados.map((pag, idx) => (
                      <li key={`pag-${idx}`}>
                        <strong>{formatCurrency(pag.valor)}</strong>
                        {' · '}
                        {modoPagamentoLabel(pag.modo_pagamento)}
                        {pag.registrado_em ? ` · ${formatDate(pag.registrado_em)}` : ''}
                        {Array.isArray(pag.comprovantes) && pag.comprovantes.length > 0
                          ? ` · ${pag.comprovantes.length} comprovante(s)`
                          : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {viewItem.descricao ? (
                <div className="cobrancas-detail-item" style={{ marginBottom: '0.75rem' }}>
                  <span>Descrição</span>
                  <strong>{viewItem.descricao}</strong>
                </div>
              ) : null}
              <div className="cobrancas-detail-item" style={{ marginBottom: '0.75rem' }}>
                <span>Link de pagamento</span>
              </div>
              <div className="cobrancas-link-box">
                <a
                  href={getPaymentUrl(viewItem, settings)}
                  target="_blank"
                  rel="noreferrer"
                >
                  {getPaymentUrl(viewItem, settings)}
                </a>
                <button
                  type="button"
                  className="btn-icon"
                  title="Copiar"
                  onClick={() =>
                    copyText(getPaymentUrl(viewItem, settings), 'Link copiado')
                  }
                >
                  <HiDuplicate size={16} />
                </button>
              </div>
              <div className="form-group">
                <label>PIX Copia e Cola</label>
                <textarea readOnly rows={4} value={viewItem.pix_payload || ''} />
                <button
                  type="button"
                  className="btn-cob-muted"
                  style={{ marginTop: '0.5rem' }}
                  onClick={() => copyText(viewItem.pix_payload, 'PIX copiado')}
                >
                  Copiar PIX
                </button>
              </div>
            </div>
            <div className="modal-footer">
              {canManage &&
                viewItem.status !== 'paga' &&
                viewItem.status !== 'cancelada' && (
                  <>
                    <button
                      type="button"
                      className="btn-cob-muted"
                      onClick={() => handleSendWhatsApp(viewItem)}
                    >
                      Enviar WhatsApp
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => requestMarkPaid(viewItem)}
                    >
                      {viewItem.status === 'incompleto' ? 'Registrar pagamento' : 'Confirmar pagamento'}
                    </button>
                    <button
                      type="button"
                      className="btn-cob-muted"
                      onClick={() => handleCancel(viewItem)}
                    >
                      Cancelar cobrança
                    </button>
                  </>
                )}
              <button
                type="button"
                className="btn-cob-muted"
                onClick={() => setViewItem(null)}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmAction?.type === 'paid' && confirmAction.item && (
        <div className="modal-overlay" onClick={() => !confirmingPaid && setConfirmAction(null)}>
          <div
            className="modal-content cobrancas-confirm-paid"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Registrar pagamento</h2>
              <button
                type="button"
                className="modal-close"
                disabled={confirmingPaid}
                onClick={() => setConfirmAction(null)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p className="cobrancas-confirm-paid__intro">
                <strong>{confirmAction.item.titulo}</strong>
                <br />
                Total {formatCurrency(confirmAction.item.valor)} · Pago{' '}
                {formatCurrency(confirmAction.item.valor_pago || 0)} · Restante{' '}
                <strong>{formatCurrency(remainingAmount(confirmAction.item))}</strong>
              </p>

              <div className="form-group">
                <label>Valor deste pagamento *</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={confirmPaidForm.valor}
                  onChange={(e) =>
                    setConfirmPaidForm((prev) => ({
                      ...prev,
                      valor: maskCurrencyInput(e.target.value),
                    }))
                  }
                  placeholder="R$ 0,00"
                  disabled={confirmingPaid}
                  required
                />
                <small className="muted">
                  Pode ser parcial. Máximo: {formatCurrency(remainingAmount(confirmAction.item))}
                </small>
              </div>

              <div className="form-group">
                <label>Modo de pagamento *</label>
                <select
                  value={confirmPaidForm.modo_pagamento}
                  onChange={(e) =>
                    setConfirmPaidForm((prev) => ({
                      ...prev,
                      modo_pagamento: e.target.value,
                    }))
                  }
                  disabled={confirmingPaid}
                  required
                >
                  {MODO_PAGAMENTO_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Comprovantes *</label>
                <label
                  htmlFor="cobranca-comprovante-upload"
                  className="cobrancas-comprovante-upload"
                >
                  <HiCloudUpload size={20} />
                  <span>Adicionar PDF ou imagens</span>
                  <input
                    id="cobranca-comprovante-upload"
                    type="file"
                    accept="image/*,.pdf,application/pdf"
                    multiple
                    onChange={handleComprovanteChange}
                    disabled={confirmingPaid}
                    style={{ display: 'none' }}
                  />
                </label>
                {(confirmPaidForm.comprovantes || []).length > 0 ? (
                  <div className="cobrancas-comprovante-files">
                    {confirmPaidForm.comprovantes.map((entry, idx) => (
                      <div key={`${entry.name}-${idx}`} className="cobrancas-comprovante-preview">
                        {entry.preview ? (
                          <img src={entry.preview} alt={entry.name} />
                        ) : (
                          <span>{entry.name}</span>
                        )}
                        <button
                          type="button"
                          className="btn-cob-muted"
                          disabled={confirmingPaid}
                          onClick={() => removeComprovanteAt(idx)}
                        >
                          Remover
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn-cob-muted"
                      disabled={confirmingPaid}
                      onClick={clearComprovantes}
                    >
                      Limpar todos
                    </button>
                  </div>
                ) : (
                  <small className="muted">Pode anexar vários arquivos · PDF/JPG/PNG/WEBP · até 10 MB cada</small>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-cob-muted"
                disabled={confirmingPaid}
                onClick={() => setConfirmAction(null)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={
                  confirmingPaid ||
                  !confirmPaidForm.modo_pagamento ||
                  !(confirmPaidForm.comprovantes || []).some((c) => c.file instanceof File) ||
                  !(parseMoneyInput(confirmPaidForm.valor) > 0)
                }
                onClick={handleConfirmAction}
              >
                {confirmingPaid ? 'Registrando…' : 'Registrar pagamento'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmAction?.type === 'delete' && confirmAction.item && (
        <ConfirmDialog
          isOpen
          title="Excluir cobrança"
          message={`Deseja realmente excluir a cobrança "${confirmAction.item.titulo}"? Esta ação não pode ser desfeita.`}
          confirmText="Sim, excluir"
          cancelText="Não"
          type="danger"
          onConfirm={handleConfirmAction}
          onCancel={() => setConfirmAction(null)}
        />
      )}

      <div className="notifications-container">
        {notifications.map((notification) => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            duration={notification.duration}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default Cobrancas;
